import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Container, useMediaQuery, useTheme } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EdenAutoPromoSection from './EdenAutoPromoSection';
import MMAPromoSection from './MMAPromoSection';
import EdenAutoPromoMobile from './EdenAutoPromoMobile';
import MMAPromoMobile from './MMAPromoMobile';
import { FONT_FAMILY } from '../common/sharedFunctions';
import { useTranslation } from 'react-i18next';

const PartnersPromoSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const partners = isMobile 
    ? [EdenAutoPromoMobile, MMAPromoMobile]
    : [EdenAutoPromoSection, MMAPromoSection];
  const { i18n } = useTranslation();

  // Auto-slide every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % partners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [partners.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % partners.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + partners.length) % partners.length);
  };

  const CurrentComponent = partners[currentSlide];

  return (
    <Box sx={{ backgroundColor: '#000' }}>
      {/* Section Title */}
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 8 } }}>
        <Typography
          variant="h2"
          align="center"
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: { xs: '28px', md: '42px' },
            fontWeight: 800,
            color: '#D4AF37',
            mb: { xs: 4, md: 6 },
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}
        >
          {i18n.language === 'fr' ? 'Nos Partenaires' : 'Our Partners'}
        </Typography>
      </Container>

      {/* Slider */}
      <Box sx={{ position: 'relative' }}>
      {/* Navigation Arrows */}
      <IconButton
        onClick={prevSlide}
        sx={{
          position: 'absolute',
          left: { xs: 5, md: 20 },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          backgroundColor: 'rgba(212, 175, 55, 0.2)',
          color: '#D4AF37',
          width: { xs: 40, md: 48 },
          height: { xs: 40, md: 48 },
          border: '1px solid rgba(212, 175, 55, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(212, 175, 55, 0.3)',
            borderColor: '#D4AF37'
          }
        }}
      >
        <ArrowBackIosNewIcon sx={{ fontSize: { xs: '1rem', md: '1.5rem' } }} />
      </IconButton>

      <IconButton
        onClick={nextSlide}
        sx={{
          position: 'absolute',
          right: { xs: 5, md: 20 },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          backgroundColor: 'rgba(212, 175, 55, 0.2)',
          color: '#D4AF37',
          width: { xs: 40, md: 48 },
          height: { xs: 40, md: 48 },
          border: '1px solid rgba(212, 175, 55, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(212, 175, 55, 0.3)',
            borderColor: '#D4AF37'
          }
        }}
      >
        <ArrowForwardIosIcon sx={{ fontSize: { xs: '1rem', md: '1.5rem' } }} />
      </IconButton>

      {/* Slider Content */}
      <Box
        sx={{
          overflow: 'hidden',
          position: 'relative',
          px: { xs: 2, md: 0 }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: `translateX(-${currentSlide * 100}%)`,
            gap: { xs: 2, md: 0 }
          }}
        >
          {partners.map((Component, index) => (
            <Box 
              key={index} 
              sx={{ 
                minWidth: '100%', 
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Component />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Dots Indicator */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1.5,
          mt: { xs: 4, md: 3 },
          pb: { xs: 5, md: 8 }
        }}
      >
        {partners.map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrentSlide(index)}
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: currentSlide === index ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: currentSlide === index ? '#D4AF37' : 'rgba(212, 175, 55, 0.5)'
              }
            }}
          />
        ))}
      </Box>
      </Box>
    </Box>
  );
};

export default PartnersPromoSlider;
